import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useSnackbar } from 'notistack'
import { LoadingButton } from '@mui/lab'
import { Button, Container, Grid, ListItemText, Stack } from '@mui/material'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import SiderContentCard from '@/components/ui/cards/SiderContentCard'
import TextInputController from '@/components/ui/inputs/TextInputController'
import TextInput from '@/components/ui/inputs/TextInput'
import { Network } from '@/core/models/network.model'
import { useUpdateNetworkMutation } from '@/core/services/network'
import { getApiErrorMessage } from '@/utils/errors'
import QuillController from '@/components/ui/inputs/QuillController'

type NetworkSettingsProps = {
  network: Network
  isMobile?: boolean
}

export default function NetworkSettings(props: NetworkSettingsProps) {
  const { network, isMobile } = props
  const { t } = useTranslation()

  const initialValues = useMemo(
    () => ({
      description: network.description,
      name: network.name,
      privacy: network.privacy,
    }),
    [network]
  )

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <SiderContentCard title={isMobile ? t('page.network.about.title') : t('page.network.settings.title')}>
        <NetworkEditForm networkId={network._id} initialValues={initialValues} />
      </SiderContentCard>
    </Container>
  )
}

const networkEditFormSchema = z.object({
  description: z.string().optional(),
  name: z.string().min(1),
  privacy: z.enum(['public', 'private']),
})

type NetworkEditFormValues = z.infer<typeof networkEditFormSchema>

function NetworkEditForm({ initialValues, networkId }: { initialValues?: NetworkEditFormValues; networkId: string }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [updateNetwork, { isLoading: isUpdatingNetwork }] = useUpdateNetworkMutation()

  const { control, handleSubmit, reset } = useForm<NetworkEditFormValues>({ defaultValues: initialValues, resolver: zodResolver(networkEditFormSchema) })

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const privacyOptions = useMemo(
    () => [
      {
        label: <ListItemText primary={t('page.network.privacy.public.primary')} secondary={t('page.network.privacy.public.description')} sx={{ pl: 1 }} />,
        value: 'public',
      },
      {
        label: <ListItemText primary={t('page.network.privacy.private.primary')} secondary={t('page.network.privacy.private.description')} sx={{ pl: 1 }} />,
        value: 'private',
      },
    ],
    [t]
  )

  const [editable, setEditable] = useState<boolean>(false)

  const onSubmit: SubmitHandler<NetworkEditFormValues> = async (values) => {
    try {
      await updateNetwork({ id: networkId, ...values }).unwrap()
      setEditable(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.network.update.error')), { variant: 'error' })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={!editable} />
        </Grid>
        <Grid item xs={12}>
          <div>
            <QuillController control={control} label={t('field.description.label')} name="description" readOnly={!editable} />
          </div>
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="privacy"
            render={({ field }) =>
              editable ? (
                <RadioGroupInput
                  {...field}
                  fullWidth
                  label={t('field.privacy.label')}
                  options={privacyOptions}
                  variant="contained"
                  sx={{ '& .MuiFormControlLabel-root': { p: 1 } }}
                />
              ) : (
                <TextInput fullWidth label={t('field.privacy.label')} readOnly value={t(`page.network.privacy.${field.value}.primary`)} />
              )
            }
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="end" sx={{ mt: '36px' }}>
        {editable ? (
          <Stack direction="row" spacing={2}>
            <Button
              variant="neutral"
              onClick={() => {
                reset()
                setEditable(false)
              }}
            >
              {t('common:button.cancel.label')}
            </Button>
            <LoadingButton color="primary" loading={isUpdatingNetwork} disableElevation type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          </Stack>
        ) : (
          <Button color="primary" disableElevation variant="contained" onClick={() => setEditable(true)}>
            {t('common:button.modify.label')}
          </Button>
        )}
      </Stack>
    </form>
  )
}
